import { dataFetch, billingFetch } from '../api';
import projectManager from './ProjectManager';

export const PLAN_IDS = {
  FREE: 'FREE',
  PRO_30_DAYS: 'PRO_30_DAYS',
  PRO_MONTHLY: 'PRO_MONTHLY',
  PRO_YEARLY: 'PRO_YEARLY'
};

const ACTIVE_PLANS = new Set([
  PLAN_IDS.PRO_30_DAYS,
  PLAN_IDS.PRO_MONTHLY,
  PLAN_IDS.PRO_YEARLY
]);

const PAYMENT_DRAFT_KEY = 'floorcraft-payment-draft';

class SubscriptionManager {
  async getActiveSubscription(userId) {
    if (!userId) return null;

    const response = await dataFetch(
      `read/subscriptions?owner_id=${encodeURIComponent(userId)}&user_id=${encodeURIComponent(userId)}&status=active&sort=updated_at&order=desc`,
      { method: 'GET' }
    );

    if (response?.status !== 'success') return null;

    const subscriptions = Array.isArray(response?.data) ? response.data : [];
    const orderedSubscriptions = [...subscriptions].sort((left, right) => {
      const leftUpdatedAt = new Date(left?.updated_at ?? '').getTime();
      const rightUpdatedAt = new Date(right?.updated_at ?? '').getTime();

      if (rightUpdatedAt !== leftUpdatedAt) {
        return rightUpdatedAt - leftUpdatedAt;
      }

      return Number(right?.id ?? 0) - Number(left?.id ?? 0);
    });

    const activeSubscription = orderedSubscriptions.find((subscription) => {
      const planId = subscription?.plan_id ?? '';
      const status = subscription?.status ?? '';
      const validUntil = subscription?.valid_until ?? '';
      const hasNotExpired = validUntil && new Date(validUntil).getTime() > Date.now();

      return status === 'active' && ACTIVE_PLANS.has(planId) && hasNotExpired;
    });

    if (!activeSubscription) return null;

    return {
      id: activeSubscription?.id ?? null,
      userId: activeSubscription?.user_id ?? userId,
      planId: activeSubscription?.plan_id ?? '',
      status: activeSubscription?.status ?? 'active',
      validUntil: activeSubscription?.valid_until ?? '',
      paymentReference: activeSubscription?.payment_reference ?? '',
      updatedAt: activeSubscription?.updated_at ?? ''
    };
  }

  preserveCurrentProject() {
    try {
      sessionStorage.setItem(
        PAYMENT_DRAFT_KEY,
        JSON.stringify(projectManager.getProjectState())
      );
    } catch (error) {
      console.error('Nie udało się zachować rzutu przed płatnością.', error);
    }
  }

  async startCheckout(planId) {
    const response = await billingFetch('create-checkout', {
      method: 'POST',
      body: JSON.stringify({
        planId,
        projectState: projectManager.getProjectState()
      })
    });

    if (response?.status !== 'success' || !response?.data?.checkoutUrl) {
      return {
        success: false,
        error: response?.error || 'Nie udało się rozpocząć płatności.'
      };
    }

    window.location.assign(response.data.checkoutUrl);
    return { success: true };
  }

  async cancelSubscription() {
    const response = await billingFetch('cancel-subscription', {
      method: 'POST',
      body: JSON.stringify({})
    });

    if (response?.status !== 'success') {
      return {
        success: false,
        error: response?.error || 'Nie udało się anulować subskrypcji.'
      };
    }

    return { success: true };
  }

  restorePendingProject() {
    try {
      const serializedDraft = sessionStorage.getItem(PAYMENT_DRAFT_KEY);
      if (!serializedDraft) return false;

      projectManager.loadState(JSON.parse(serializedDraft));
      sessionStorage.removeItem(PAYMENT_DRAFT_KEY);
      return true;
    } catch (error) {
      console.error('Nie udało się przywrócić rzutu po płatności.', error);
      return false;
    }
  }
}

export default new SubscriptionManager();